import React, { useState, useEffect, useCallback, useMemo } from "react";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import { formatDate } from "../../utils/date";
import styles from "./Products.module.scss";
import ProductsService from "../../api/services/products.service";
import { ProductsTypes } from "../../api/types/products.types";
import Loading from "../../components/common/loading/Loading";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const getLocalizedName = (row: any, lang: string): string => {
  const priorities: Record<string, (keyof typeof row)[]> = {
    az: ["name_az", "name_en", "name_de", "name_es"],
    en: ["name_en", "name_az", "name_de", "name_es"],
    es: ["name_es", "name_en", "name_az", "name_de"],
    de: ["name_de", "name_en", "name_az", "name_es"],
  };
  const order = priorities[lang] || priorities["en"];
  for (const key of order) {
    const value = row[key];
    if (value && typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return "Unnamed Variant";
};

const LOW_STOCK_LIMIT = 10;

const LowStockProducts = () => {
  const { t } = useTranslation("translation");
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const getProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProductsService.getAllProductVariants(page, limit);
      const filtered = res.data.data.filter(
        (p: ProductsTypes) => p.stock_quantity < LOW_STOCK_LIMIT,
      );
      setProducts(filtered);
      setCount(res.data.count);
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const columns: Column[] = [
    { key: "sku", header: t("columns.sku") ?? "Sku" },
    {
      key: "name",
      header: t("columns.name") ?? "Name",
      render: (_, row) => {
        const productName = getLocalizedName(row, i18n.language);
        return <p className={styles.content}>{productName}</p>;
      },
    },
    {
      key: "category",
      header: t("columns.category") ?? "Category",
      render: (_, row) => {
        const category = row.product.category;
        const lang = i18n.language;
        let categoryName: string | undefined;
        if (category) {
          switch (lang) {
            case "en":
              categoryName = category.name_en;
              break;
            case "az":
              categoryName = category.name_az;
              break;
            case "de":
              categoryName = category.name_de;
              break;
            case "es":
              categoryName = category.name_es;
              break;
            default:
              categoryName = category.name;
          }
          if (!categoryName) categoryName = category.name;
        }
        return categoryName || "-";
      },
    },
    {
      key: "brand",
      header: t("columns.brand") ?? "Brand",
      render: (_, row) => row.product.brand.name || "-",
    },
    {
      key: "price",
      header: t("columns.price") ?? "Price",
      render: (p, row) => (
        <p className={styles.price_content}>
          {`${p} ${row?.product?.currency?.name}` || "-"}
        </p>
      ),
    },
    {
      key: "discount_percent",
      header: t("labels.discount_percent") ?? "Discount Percent",
      render: (p) => <p className={styles.content}>{`${p}%` || "-"}</p>,
    },
    {
      key: "discount_price",
      header: t("labels.discounted_price") ?? "Discount Price",
      render: (p, row) => (
        <p className={styles.content}>
          {`${p} ${row?.product?.currency?.name}` || "-"}
        </p>
      ),
    },
    {
      key: "stock_quantity",
      header: t("columns.stock") ?? "Stock",
      render: (p) => <span style={{ color: "red", fontWeight: 600 }}>{p}</span>,
    },
    {
      key: "images",
      header: t("columns.images") ?? "Images",
      render: (imgs) => {
        return imgs?.slice(0, 2)?.map((img: any, index: number) => {
          return img.image ? (
            <div
              key={index}
              style={{
                cursor: "pointer",
                display: "inline-block",
                marginRight: "5px",
              }}
            >
              <img
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                src={img.image}
                alt={"products"}
              />
            </div>
          ) : null;
        });
      },
    },
    {
      key: "created",
      header: t("columns.date") ?? "Date",
      render: (v) => formatDate(v, true),
    },
  ];

  if (loading) return <Loading />;

  return (
    <div className={styles.products}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 24 }}>
        {t("quick_actions_low_stock")}
      </h1>
      <p style={{ fontSize: 16, color: "#888", marginBottom: 32 }}>
        {t("quick_actions_low_stock_sub")}
      </p>
      <DataTable
        data={products}
        columns={columns}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
        noDataMessage={t("no_products") || ""}
      />
    </div>
  );
};

export default LowStockProducts;
